import { ArrowRight, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { z } from "zod";
import { setAuthToken, setUserEmail } from "../services/auth";
import { Button } from "./ui/button";
import { Toaster } from "./ui/toaster";
import { toast } from "./ui/use-toast";
import { sendOtp } from "../services/sendOtp";
import { verifyOtp } from "../services/verifyOtp";

interface LoginProps {
  setAuth: (auth: boolean) => void;
  onLoginSuccess: () => void;
}

type LoginStage = "email" | "otp" | "done";

const Login: React.FC<LoginProps> = ({ setAuth, onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState<LoginStage>("email");
  const [generatingOtp, setGeneratingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [emailLogin, setEmailLogin] = useState(false);

  async function continueToOtp() {
    // validate email

    const isValid = z.string().email().safeParse(email).success;
    if (!isValid) {
      console.log("Invalid email");
      return;
    }

    // send otp
    setGeneratingOtp(true);
    const sentRes = await sendOtp(email);
    const body = (await sentRes.json()) as { message: string };
    if (sentRes && sentRes.ok) {
      if (body.message) {
        toast({
          title: "OTP sent",
          description: body.message,
        });
      }
      setStage("otp");
    } else {
      toast({
        title: "Error sending OTP",
        description: body.message,
      });
    }
    setGeneratingOtp(false);
  }

  async function continueToHome() {
    // verify otp and login
    setVerifyingOtp(true);
    const res = await verifyOtp(email, otp);
    if (res && res.ok) {
      const body = (await res.json()) as { access_token: string };
      if (body.access_token) {
        setAuthToken(body.access_token);
        setUserEmail(email);
        setAuth(true);
      }
    } else {
      const body = (await res.json()) as { detail: string };
      const detail = body.detail;
      if (detail) {
        toast({
          title: "Error verifying OTP",
          description: detail,
        });
      }
    }
    setVerifyingOtp(false);
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="flex flex-col items-center justify-center max-w-[400px]">
        <div className="h-stack gap-3">
          <img src="/logo.png" alt="company logo" />
          <h1 className="font-medium text-zinc-700 text-lg">
            Welcome to FlashDocs
          </h1>
        </div>
        <p className="text-sm text-zinc-500 mt-2 text-center">
          FlashDocs is a tool for creating and sharing <br></br>presentations
          with your team
        </p>
        {emailLogin ? (
          <div>
            {stage === "email" && (
              <div className="flex flex-col items-center justify-center w-full">
                <input
                  type="text"
                  className="border border-zinc-300 rounded-md p-2 mt-6 w-[400px]"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {generatingOtp ? (
                  <Button
                    className="my-3 w-full group transition-all text-white flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={generatingOtp}
                  >
                    <Loader2 size={18} className="animate-spin" />
                  </Button>
                ) : (
                  <Button
                    className="my-3 w-full group transition-all"
                    onClick={continueToOtp}
                  >
                    Continue
                    <ArrowRight
                      size={14}
                      className="ml-2 hidden group-hover:block transition-all"
                    />
                  </Button>
                )}
              </div>
            )}
            {stage === "otp" && (
              <div className="flex flex-col items-center justify-center">
                <p className="text-sm text-zinc-500 mt-6 text-center">
                  We have sent a one time password to your email from
                  flashy@flashdocs.ai. Please enter the code below to login.
                </p>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  type="text"
                  className="border border-zinc-200 rounded-md mt-5 px-4 py-2 w-full text-center"
                  placeholder="OTP"
                />
                {verifyingOtp ? (
                  <Button
                    className="my-3 w-full group transition-all text-white flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={verifyingOtp}
                  >
                    <Loader2 size={18} className="animate-spin" />
                  </Button>
                ) : (
                  <Button
                    className="my-3 w-full group transition-all"
                    onClick={continueToHome}
                  >
                    Continue
                    <ArrowRight
                      size={14}
                      className="ml-2 hidden group-hover:block transition-all"
                    />
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <Button className="my-2" onClick={onLoginSuccess}>
              <div className="h-stack gap-3">
                <img className="w-6 h-6" src="/google.png" alt="google logo" />
                <p>Sign in with Google</p>
              </div>
            </Button>

            <div>
              <p
                onClick={() => {
                  setEmailLogin(true);
                }}
                role="link"
                className="text-zinc-500 text-center text-sm underline cursor-pointer"
              >
                Login with email
              </p>
            </div>
          </div>
        )}
      </div>
      <Toaster />
    </div>
  );
};

export default Login;
